import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Link from 'next/link';
import { GateFormData } from '../types';
import { QUESTIONS } from '../constants';
import AssessmentFooter from '../AssessmentFooter';
import { ArrowRight } from '../icons';
import { NewLogo } from '@/components/ui/icons/Logo';
import { useAssessmentContent } from '../content/context';

interface GateScreenProps {
  onStart: (data: GateFormData) => void;
  onBack?: () => void;
}

const validationSchema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Enter a valid work email').required('Email is required'),
  company: Yup.string().required('Company name is required'),
});

const GateScreen: React.FC<GateScreenProps> = ({ onStart, onBack }) => {
  const { gate } = useAssessmentContent();
  const total = QUESTIONS.length + 1;
  const currentStep = total;
  const pct = Math.round(((currentStep - 1) / total) * 100);

  const formik = useFormik<GateFormData>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      company: '',
      companySize: '',
      role: gate.roleOptions[0] ?? 'Product leader',
    },
    validationSchema,
    onSubmit: onStart,
  });

  const fieldError = (name: keyof GateFormData) =>
    formik.touched[name] && formik.errors[name] ? formik.errors[name] : undefined;

  const inputCls = (name: keyof GateFormData) =>
    `w-full h-10 rounded-[6px] border px-3 text-a-navy font-sans bg-white outline-none transition-colors ${
      fieldError(name) ? 'border-red-400' : 'border-a-border'
    }`;

  return (
    <div className="animate-fade-in">
      <nav
        className="bg-white border-b border-a-border px-10 flex items-center justify-between sticky top-0 z-50"
        style={{ height: '58px' }}
      >
        <Link href="/">
          <NewLogo height="60" title="Foxbox Digital" />
        </Link>
        <div className="font-semibold text-a-navy" style={{ fontSize: '11px' }}>
          Question {currentStep} of {total}
        </div>
      </nav>

      <div
        className="bg-a-off-white flex flex-col items-center p-12"
        style={{ minHeight: 'calc(100vh - 58px - 42px)' }}
      >
        <form
          onSubmit={formik.handleSubmit}
          noValidate
          className="bg-white border border-a-border w-full"
          style={{
            padding: '40px 44px',
            maxWidth: '640px',
            borderRadius: '10px',
          }}
        >
          {/* Progress */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold text-a-text-secondary" style={{ fontSize: '11px' }}>
                Question {currentStep} of {total}
              </span>
              <span className="font-bold text-a-navy" style={{ fontSize: '11px' }}>
                {pct}% complete
              </span>
            </div>
            <div className="bg-a-light-gray overflow-hidden" style={{ height: '5px', borderRadius: '3px' }}>
              <div
                className="bg-a-orange h-full transition-all duration-300"
                style={{ width: `${pct}%`, borderRadius: '3px' }}
              />
            </div>
          </div>

          {/* Category */}
          <div
            className="inline-flex items-center bg-a-light-gray text-a-navy font-bold uppercase mb-4"
            style={{
              fontSize: '9px',
              letterSpacing: '0.07em',
              padding: '4px 10px',
              borderRadius: '4px',
              marginBottom: '16px',
              gap: '6px',
            }}
          >
            <div
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: '#f88143',
                flexShrink: 0,
              }}
            />
            <span>{gate.categoryLabel}</span>
          </div>

          {/* Title */}
          <div
            className="font-bold text-a-navy mb-2"
            style={{
              fontSize: '19px',
              lineHeight: '1.35',
              marginBottom: '6px',
            }}
          >
            {gate.title}
          </div>
          <div className="text-a-text-muted" style={{ fontSize: '12.5px', marginBottom: '24px' }}>
            {gate.subtitle}
          </div>

          {/* Form fields */}
          <div className="grid grid-cols-2 gap-3">
            <div style={{ marginBottom: '14px' }}>
              <label
                htmlFor="firstName"
                className="block text-a-navy font-bold uppercase"
                style={{
                  fontSize: '10px',
                  letterSpacing: '0.04em',
                  marginBottom: '5px',
                }}
              >
                {gate.firstNameLabel}
              </label>
              <input
                id="firstName"
                type="text"
                placeholder={gate.firstNamePlaceholder}
                className={inputCls('firstName')}
                style={{ fontSize: '13px' }}
                {...formik.getFieldProps('firstName')}
              />
              {fieldError('firstName') && (
                <div className="mt-1 font-medium" style={{ fontSize: '10px', color: '#e05252' }}>
                  {fieldError('firstName')}
                </div>
              )}
            </div>
            <div style={{ marginBottom: '14px' }}>
              <label
                htmlFor="lastName"
                className="block text-a-navy font-bold uppercase"
                style={{
                  fontSize: '10px',
                  letterSpacing: '0.04em',
                  marginBottom: '5px',
                }}
              >
                {gate.lastNameLabel}
              </label>
              <input
                id="lastName"
                type="text"
                placeholder={gate.lastNamePlaceholder}
                className={inputCls('lastName')}
                style={{ fontSize: '13px' }}
                {...formik.getFieldProps('lastName')}
              />
              {fieldError('lastName') && (
                <div className="mt-1 font-medium" style={{ fontSize: '10px', color: '#e05252' }}>
                  {fieldError('lastName')}
                </div>
              )}
            </div>
          </div>

          <div style={{ marginBottom: '14px' }}>
            <label
              htmlFor="email"
              className="block text-a-navy font-bold uppercase"
              style={{
                fontSize: '10px',
                letterSpacing: '0.04em',
                marginBottom: '5px',
              }}
            >
              {gate.emailLabel}
            </label>
            <input
              id="email"
              type="email"
              placeholder={gate.emailPlaceholder}
              className={inputCls('email')}
              style={{ fontSize: '13px' }}
              {...formik.getFieldProps('email')}
            />
            {fieldError('email') && (
              <div className="mt-1 font-medium" style={{ fontSize: '10px', color: '#e05252' }}>
                {fieldError('email')}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '14px' }}>
            <label
              htmlFor="company"
              className="block text-a-navy font-bold uppercase"
              style={{
                fontSize: '10px',
                letterSpacing: '0.04em',
                marginBottom: '5px',
              }}
            >
              {gate.companyLabel}
            </label>
            <input
              id="company"
              type="text"
              placeholder={gate.companyPlaceholder}
              className={inputCls('company')}
              style={{ fontSize: '13px' }}
              {...formik.getFieldProps('company')}
            />
            {fieldError('company') && (
              <div className="mt-1 font-medium" style={{ fontSize: '10px', color: '#e05252' }}>
                {fieldError('company')}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3" style={{ marginBottom: '8px' }}>
            <div>
              <label
                htmlFor="companySize"
                className="block text-a-navy font-bold uppercase"
                style={{
                  fontSize: '10px',
                  letterSpacing: '0.04em',
                  marginBottom: '5px',
                }}
              >
                {gate.companySizeLabel}
              </label>
              {/* Options are intentionally hardcoded: their exact values feed
                  the HubSpot `numemployees` mapping in /api/submit-assessment. */}
              <select
                id="companySize"
                className="w-full h-10 border border-a-border rounded-[6px] px-3 text-a-navy font-sans bg-white outline-none"
                style={{ fontSize: '13px' }}
                {...formik.getFieldProps('companySize')}
              >
                <option value="">Select</option>
                <option>1–10</option>
                <option>11–50</option>
                <option>51–200</option>
                <option>201–500</option>
                <option>500+</option>
              </select>
            </div>
            <div>
              <label
                htmlFor="role"
                className="block text-a-navy font-bold uppercase"
                style={{
                  fontSize: '10px',
                  letterSpacing: '0.04em',
                  marginBottom: '5px',
                }}
              >
                {gate.roleLabel}
              </label>
              <select
                id="role"
                className="w-full h-10 border border-a-border rounded-[6px] px-3 text-a-navy font-sans bg-white outline-none"
                style={{ fontSize: '13px' }}
                {...formik.getFieldProps('role')}
              >
                {gate.roleOptions.map((option) => (
                  <option key={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>

          <div
            className="text-a-text-muted"
            style={{
              fontSize: '10px',
              lineHeight: '1.5',
              marginTop: '14px',
              marginBottom: '4px',
            }}
          >
            {gate.privacyNote}
          </div>

          {/* Nav */}
          <div className="flex justify-between items-center pt-6 mt-6 border-t border-a-light-gray">
            <button
              type="button"
              className="text-a-text-muted font-medium bg-transparent border border-a-border rounded font-sans hover:border-a-navy hover:text-a-navy transition-all cursor-pointer"
              style={{
                fontSize: '12px',
                padding: '9px 18px',
                outline: 'none',
                boxShadow: 'none',
                visibility: onBack ? 'visible' : 'hidden',
              }}
              onClick={onBack}
            >
              ← Back
            </button>
            <button
              type="submit"
              className="bg-a-orange text-white font-bold font-sans rounded flex items-center cursor-pointer hover:bg-a-orange-dark transition-colors border-0"
              style={{
                fontSize: '12px',
                padding: '9px 26px',
                borderRadius: '6px',
                border: 'none',
                letterSpacing: '0.04em',
                gap: '6px',
                outline: 'none',
                boxShadow: 'none',
              }}
            >
              {gate.submitLabel} <ArrowRight width={14} height={14} />
            </button>
          </div>
        </form>
      </div>

      <AssessmentFooter />
    </div>
  );
};

export default GateScreen;
